import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Settings, ScrollText, Loader2 } from "lucide-react";
import { api, API, inr, fmtDate, fmtErr } from "@/lib/api";

const ENTITIES = [
  { key: "users", label: "Users", desc: "All registered users with contact and status details" },
  { key: "tasks", label: "Tasks", desc: "Full task list with rewards, limits and status" },
  { key: "submissions", label: "Submissions", desc: "Feedback, proof links, UPI IDs, verification & payment status" },
  { key: "rewards", label: "Rewards", desc: "Every reward with amount and payment status" },
  { key: "withdrawals", label: "Withdrawals", desc: "Payout requests with UPI, status and admin notes" },
];

export default function AdminExports() {
  const [minW, setMinW] = useState("");
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setMinW(String(r.data.min_withdrawal))).catch(() => {});
    api.get("/admin/audit-logs").then((r) => setLogs(r.data)).catch(() => {});
  }, []);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", { min_withdrawal: parseFloat(minW) });
      setMinW(String(data.min_withdrawal));
      toast.success("Minimum withdrawal updated");
    } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
  };

  const download = (entity, format) => {
    window.open(`${API}/admin/export/${entity}?format=${format}`, "_blank");
  };

  return (
    <div data-testid="admin-exports-page">
      <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900">Exports & Settings</h1>
      <p className="mt-1 text-sm text-slate-600">Download platform data as CSV or Excel, and configure platform rules.</p>

      <div className="mt-6 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-5 sm:p-6 shadow-lg flex flex-col sm:flex-row sm:items-center gap-4" data-testid="payout-sheet-card">
        <div className="w-11 h-11 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center shrink-0">
          <FileSpreadsheet className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-bold text-white text-lg">Payout Sheet — Auto-updated</h3>
          <p className="text-sm text-blue-100 mt-1 leading-relaxed">Jaise-jaise users submit karte hain, data automatically store hota hai. Ek click mein Excel download karo: <strong className="text-white">User Name, Task ID, App Name, UPI ID</strong>, status aur reward.</p>
        </div>
        <button onClick={() => download("payout_sheet", "xlsx")} data-testid="download-payout-sheet-btn"
          className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-blue-700 bg-white hover:bg-blue-50 transition-colors shadow-md shrink-0">
          <Download className="w-4 h-4" /> Download Excel Sheet
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="export-cards">
        {ENTITIES.map((e) => (
          <div key={e.key} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5" data-testid={`export-card-${e.key}`}>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><FileSpreadsheet className="w-4.5 h-4.5 w-[18px] h-[18px]" /></div>
              <h3 className="font-heading font-semibold text-slate-900">{e.label}</h3>
            </div>
            <p className="mt-2 text-xs text-slate-500 leading-relaxed">{e.desc}</p>
            <div className="mt-4 flex gap-2">
              <button onClick={() => download(e.key, "csv")} data-testid={`export-${e.key}-csv-btn`} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors">
                <Download className="w-3.5 h-3.5" /> CSV
              </button>
              <button onClick={() => download(e.key, "xlsx")} data-testid={`export-${e.key}-xlsx-btn`} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 transition-colors">
                <Download className="w-3.5 h-3.5" /> Excel
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 sm:p-6 max-w-md" data-testid="settings-card">
        <div className="flex items-center gap-2.5">
          <Settings className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-bold font-heading text-slate-900">Platform Settings</h2>
        </div>
        <form onSubmit={saveSettings} className="mt-4 flex gap-3">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Minimum withdrawal (₹)</label>
            <input data-testid="min-withdrawal-input" type="number" min="0" step="1" required value={minW} onChange={(e) => setMinW(e.target.value)} placeholder="e.g. 200"
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <button type="submit" data-testid="settings-save-btn" disabled={saving} className="self-end inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
          </button>
        </form>
      </div>

      <div className="mt-8 bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden" data-testid="audit-log-card">
        <div className="flex items-center gap-2.5 p-5 sm:p-6 pb-3">
          <ScrollText className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-bold font-heading text-slate-900">Audit Log</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="audit-log-table">
            <thead>
              <tr className="border-y border-slate-100 bg-slate-50/60 text-left">
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Time</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Admin</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Action</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Object</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Change</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logs.map((l) => (
                <tr key={l.id} data-testid={`audit-row-${l.id}`}>
                  <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                  <td className="px-5 py-3 text-slate-700">{l.admin_email}</td>
                  <td className="px-5 py-3 font-medium text-slate-900">{l.action}</td>
                  <td className="px-5 py-3 text-slate-600">{l.object_type} · {l.object_id}</td>
                  <td className="px-5 py-3 text-slate-500 text-xs">{l.previous_value ?? "—"} → {l.new_value ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {logs.length === 0 && <p className="p-8 text-center text-sm text-slate-500">No audit entries yet.</p>}
        </div>
      </div>
    </div>
  );
}
